import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // Add timeout to prevent hanging requests
  timeout: 30000,
});

// Track which error toasts we've shown recently to prevent duplicates
const recentToasts = new Set();
const TOAST_COOLDOWN_MS = 3000;

function showToastOnce(message) {
  const key = `${message}-${Date.now()}`;
  if (recentToasts.has(message)) return;
  
  recentToasts.add(message);
  toast.error(message);
  
  setTimeout(() => {
    recentToasts.delete(message);
  }, TOAST_COOLDOWN_MS);
}

// attach token from localStorage if present (fallback to cookie)
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("rs_token");
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Global response error handler
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors (no response from server)
    if (!error.response) {
      console.error("Network error:", error.message);
      showToastOnce("Network error. Please check your connection.");
      return Promise.reject(error);
    }

    // Handle specific HTTP status codes
    const status = error.response.status;
    const detail = error.response.data?.detail || error.response.data?.error;

    switch (status) {
      case 401:
        // Unauthorized - clear token and let auth flow handle redirect
        localStorage.removeItem("rs_token");
        showToastOnce("Session expired. Please sign in again.");
        break;
      case 403:
        showToastOnce("You don't have permission to perform this action.");
        break;
      case 404:
        // Don't show toast for 404s - let components handle
        break;
      case 422:
        showToastOnce(formatApiError(detail) || "Invalid input. Please check your data.");
        break;
      case 429:
        showToastOnce("Too many requests. Please slow down.");
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        showToastOnce("Server error. Please try again later.");
        break;
      default:
        // For other errors, don't show toast - let components handle
        break;
    }

    return Promise.reject(error);
  }
);

/**
 * Format API error messages into user-friendly strings
 * Handles various error formats: string, array, object with msg property
 */
export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
